import domtoimage from 'dom-to-image';
import jsPDF from 'jspdf';
import moment from 'moment';
import React, { useRef, useState } from 'react';
import { GrDocumentPdf } from 'react-icons/gr';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import logo from '../../../../assets/images/landing-logo.svg';
import { RootState } from '../../../../store/rootReducer';

import BackDrop from '../../../common/BackDrop';
import HSModal from '../../../common/Modal';
import { commaSeperator } from '../../../utils/validation';
import Outstanding from './TopCard/Outstanding';
import TotalInvested from './TopCard/TotalInvested';

const InvestmentPDFCreater: React.FC = () => {
    const pageSize = 20;
    const chartRef = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState(false);
    const investments = useSelector((state: RootState) => state.investments.investments);
    const user = useSelector((state: RootState) => state.user);

    const columns = [
        {
            title: 'Issuer',
            dataIndex: 'issuerName',
        },
        {
            title: 'Deal',
            dataIndex: 'dealName',
        },
        {
            title: 'Deal Type',
            dataIndex: 'dealType',
        },
        {
            title: 'Instrument Type',
            dataIndex: 'instrumentType',
        },
        {
            title: 'Investment Amount',
            dataIndex: 'investmentAmount',
        },
        {
            title: 'Investment Date',
            dataIndex: 'investmentDate',
        },
        {
            title: 'Maturity Date',
            dataIndex: 'maturityDate',
        },
        {
            title: 'Outstanding Principal',
            dataIndex: 'outstandingPrincipal',
        },
        {
            title: 'Interest Rate (p.a.)',
            dataIndex: 'interestRate',
        },
        {
            title: 'Total Distribution/Interest Paid To Date',
            dataIndex: 'totalDistribution',
        },
        {
            title: 'Status',
            dataIndex: 'status',
        },
    ];

    const savePDF = async (chartRef: React.RefObject<HTMLDivElement>) => {
        setTimeout(async () => {
            if (!chartRef.current) return;

            try {
                const pdfWidth = 210; // A4 width in mm
                const pdfHeight = 297; // A4 height in mm
                const margin = 5;

                const pdf = new jsPDF('portrait', 'mm', 'a4');
                let position = margin;

                const addDivToPDF = async (divId: string) => {
                    const div = document.getElementById(divId);
                    if (!div) return;

                    const dataUrl = await domtoimage.toPng(div);
                    const img = new Image();
                    img.src = dataUrl;
                    await new Promise((resolve) => {
                        img.onload = resolve;
                    });

                    const imgWidthMM = pdfWidth - 2 * margin;
                    const imgHeightMM = (img.height * imgWidthMM) / img.width;

                    if (position + imgHeightMM > pdfHeight - margin) {
                        pdf.addPage();
                        position = margin;
                    }

                    pdf.addImage(dataUrl, 'PNG', margin, position, imgWidthMM, imgHeightMM);
                    position += imgHeightMM + margin;
                };

                // Add each div to the PDF
                await addDivToPDF('page_1');
                await addDivToPDF('page_2');
                await addDivToPDF('page_3');
                await addDivToPDF('page_4');

                pdf.save('Investments.pdf');
                setOpen(false);
            } catch (error) {
                console.error('There was an error generating the PDF', error);
                setOpen(false);
            }
        }, 2500);
    };

    return (
        <div>
            <GrDocumentPdf
                color="#0F2240"
                size={'1.5rem'}
                className="cursor-pointer"
                onClick={() => {
                    setOpen(true);
                    savePDF(chartRef);
                }}
            />

            <HSModal className="pdf_doc" title="PDF" open={open} setOpen={setOpen} width="270mm">
                <BackDrop />
                <div ref={chartRef}>
                    {/* page 1 */}
                    <div id="page_1" className="p-4 bg-[#f1f7ff]">
                        <img src={logo} alt="CompanyLogo" className="w-56" />
                        <div className="flex justify-between items-start mt-4">
                            <div>
                                <div>
                                    <p className="text-[#142C53] font-[WorkSans-Semibold] text-3xl">
                                        Investment Summary{' '}
                                    </p>
                                    <span className="text-[#27AE60] text-sm font-[WorkSans]">
                                        Summary of all your investments as of {moment().format('DD-MMM-YYYY')}
                                    </span>
                                </div>
                            </div>
                            <div>
                                <p className="text-[#142C53] font-[WorkSans-Semibold] text-3xl">
                                    {user?.user?.userProfile?.name || user.user?.email}
                                </p>
                                <p className="WorkSans text-xs text-[#6D707B]">
                                    {user?.user?.creationAudit?.updatedAt
                                        ? `Account opened on ${moment(user.user?.creationAudit?.updatedAt).format(
                                              `DD MMM, YYYY`,
                                          )}`
                                        : ''}
                                </p>
                            </div>
                        </div>

                        <div className="mb-2">
                            <p className="font-[WorkSans-Bold] text-[#142c53] text-2xl my-4 mb-2 uppercase">
                                Outstanding Investment
                            </p>
                        </div>
                        <Outstanding isPdf={true} />
                    </div>
                    {/* page 1 end */}

                    {/* page 2 start */}
                    <div id="page_2" className="p-4 mb-2 bg-[#f1f7ff]">
                        <div className="mb-2">
                            <p className="font-[WorkSans-Bold] text-[#142c53] text-2xl mb-2 uppercase">
                                Total Invested
                            </p>
                        </div>
                        <TotalInvested isPdf={true} />
                        {/* <div className="mt-6">
                            {' '}
                            <ChartNotes />
                        </div> */}
                    </div>
                    {/* page 2 end */}

                    {/* page 3 start */}
                    <div id="page_3" className="p-4 bg-white">
                        {Array.from({ length: Math.ceil((investments || []).length / pageSize) }, (_, i) => i + 1).map(
                            (page) => (
                                <div className="investment_pdf w-[255mm]" key={uuidv4()}>
                                    <div className="mb-2">
                                        <div className="mb-2">
                                            <p className="WorkSans font-semibold text-2xl text-[#20222B] mb-2 uppercase">
                                                InvestmentS
                                            </p>
                                        </div>
                                        <table className="border-separate w-[255mm]" style={{ borderSpacing: '6px' }}>
                                            <thead>
                                                <tr className="bg-[#e2f8ec]">
                                                    {columns.map((c, index) => (
                                                        <th
                                                            className="text-xs WorkSans-Bold text-[#20222B] px-1 py-1"
                                                            key={`${c.dataIndex}_${index}_${uuidv4()}`}
                                                        >
                                                            {c.title}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(investments || [])
                                                    .slice((page - 1) * pageSize, page * pageSize)
                                                    .map((investment, index) => (
                                                        <tr
                                                            key={`${index}_${investment.uuid}_${uuidv4()}`}
                                                            className="bg-[#f1f7ff] "
                                                        >
                                                            <td className="text-xs text-[#20222B] WorkSans px-1 py-1">
                                                                {investment.issuerName || '-'}
                                                            </td>
                                                            <td className="text-xs text-[#20222B] WorkSans px-1 py-1">
                                                                {investment.dealName || '-'}
                                                            </td>
                                                            <td className="text-xs text-[#20222B] WorkSans px-1 py-1">
                                                                {investment.dealType || '-'}
                                                            </td>
                                                            <td className="text-xs text-[#20222B] WorkSans px-1 py-1">
                                                                {investment.instrumentType || '-'}
                                                            </td>
                                                            <td className="text-xs text-[#20222B] WorkSans px-1 py-1">
                                                                {investment.investmentAmount &&
                                                                    investment.investmentAmount.amountUpto2dp &&
                                                                    `${
                                                                        investment.investmentAmount.currency
                                                                    } ${commaSeperator(
                                                                        parseFloat(
                                                                            String(
                                                                                investment.investmentAmount
                                                                                    .amountUpto2dp || 0,
                                                                            ),
                                                                        ).toFixed(2),
                                                                    )}`}
                                                            </td>
                                                            <td className="text-xs text-[#20222B] WorkSans px-1 py-1">
                                                                {investment.investmentDate
                                                                    ? moment(investment.investmentDate).format(
                                                                          'DD-MMM-YY',
                                                                      )
                                                                    : 'NA'}
                                                            </td>
                                                            <td className="text-xs text-[#20222B] WorkSans px-1 py-1">
                                                                {investment.maturityDate
                                                                    ? moment(investment.maturityDate).format(
                                                                          'DD-MMM-YY',
                                                                      )
                                                                    : 'NA'}
                                                            </td>
                                                            <td className="text-xs text-[#20222B] WorkSans px-1 py-1">
                                                                {investment.outstandingPrincipal?.amountUpto2dp
                                                                    ? `${
                                                                          investment.outstandingPrincipal.currency
                                                                      } ${commaSeperator(
                                                                          parseFloat(
                                                                              String(
                                                                                  investment.outstandingPrincipal
                                                                                      .amountUpto2dp || 0,
                                                                              ),
                                                                          ).toFixed(2),
                                                                      )}`
                                                                    : 'NA'}
                                                            </td>
                                                            <td className="text-xs text-[#20222B] WorkSans px-1 py-1">
                                                                {investment.interestRate
                                                                    ? `${investment.interestRate}`
                                                                    : 'NA'}
                                                            </td>
                                                            <td className="text-xs text-[#20222B] WorkSans px-1 py-1">
                                                                {' '}
                                                                {investment.totalDistribution?.amountUpto2dp
                                                                    ? `${
                                                                          investment.totalDistribution.currency
                                                                      } ${commaSeperator(
                                                                          parseFloat(
                                                                              String(
                                                                                  investment.totalDistribution
                                                                                      .amountUpto2dp || 0,
                                                                              ),
                                                                          ).toFixed(2),
                                                                      )}`
                                                                    : 'NA'}
                                                            </td>
                                                            <td className="text-xs text-[#20222B] WorkSans px-1 py-1">
                                                                {investment.status || ''}
                                                            </td>
                                                        </tr>
                                                    ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ),
                        )}
                        {/* <div className="mt-4">
                            <InvestmentNotes />
                        </div> */}
                    </div>
                    <div id="page_4" className="p-4 bg-black">
                        <ul className="list-disc text-[#B9B9B9] font-Lato text-xs ml-6">
                            <li>
                                Exchange Rate: All principal amount have been converted to USD for comparison purposes,
                                using the FX rates provided by{' '}
                                <a
                                    href="https://openexchangerates.org"
                                    className="underline"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    https://openexchangerates.org
                                </a>{' '}
                                (refreshed twice daily at noon and midnight SGT).
                            </li>
                            <li>
                                Investment Outstanding: This represents the total principal amount that you have
                                invested via Helicap Securities that has not been repaid, redeemed, or exited to date.
                                For the avoidance of doubt, this does not represent the current value of the investment
                                to date (i.e. The amount shown does not include any interest paid).
                            </li>
                            <li>
                                Total Invested: This represents the total principal amount that you have invested
                                historically via Helicap Securities to date.
                            </li>
                            <li>
                                Currency breakdown: The principal amount invested via Helicap Securities in its
                                respective currency, has been converted to the USD equivalent to provide a view of the
                                portfolio breakdown.
                            </li>
                            <li>
                                {' '}
                                breakdown: This breakdown provides an indicative exposure based on Helicap Securities’
                                assignment of each invested deal to a specific country/region.
                            </li>
                            <li>
                                {' '}
                                in "Investment Amount", "Outstanding Investment", "Outstanding Principal" and "Total
                                Distribution/Interest Paid To Date" may take 2 to 3 business days to reflect the exact
                                state of your portfolio.
                            </li>
                            <li>
                                Information displayed under "Maturity Date", "Outstanding Principal" and "Interest Rate
                                (p.a.)" fields shall be applicable to debt investments only.
                            </li>
                            <li>
                                The "Interest rate (p.a.)" field is an indicative metric that may be subject to
                                management and administrative fees, and/or other expenses. It may differ from the net
                                interest rate you receive.
                            </li>
                            <li>
                                The "Total Distribution/Interest paid to date" field represents the total of (1)
                                dividend distribution payout for investments in Helicap’s Fund since your initial
                                subscription, and (2) the interest paid to date for debt investments via Helicap
                                Securities. Note that, as the platform was only updated on MMM.YYYY, the interest paid
                                from past debt investments may not be included.
                            </li>
                            <li>"Interest Paid To Date" is net of Management Fees.</li>
                        </ul>
                    </div>
                    {/* page 3 end */}
                </div>
            </HSModal>
        </div>
    );
};

export default InvestmentPDFCreater;
